import React from 'react';
import { Button } from 'antd';
import { InboxOutlined, PlusOutlined } from '@ant-design/icons';

/**
 * Reusable Empty State Component for Tables and Lists
 * 
 * @param {Object} props
 * @param {string} props.title - Main heading text (default: "No Data Available")
 * @param {string} props.description - Description text
 * @param {string} props.icon - Icon type: 'inbox', 'package', 'empty' (default: 'inbox')
 * @param {Function} props.onAction - Click handler for action button
 * @param {string} props.actionText - Action button text
 * @param {React.ReactNode} props.actionIcon - Action button icon
 * @param {string} props.gradient - Gradient type: 'purple', 'pink', 'blue', 'orange' (default: 'purple')
 * @param {boolean} props.showAction - Whether to show action button (default: true if onAction provided)
 * @param {string} props.size - Size variant: 'small', 'medium', 'large' (default: 'medium')
 */
const EmptyState = ({
    title = "No Data Available",
    description = "There are no items to display at the moment.",
    icon = 'inbox',
    onAction,
    actionText = "Add New",
    actionIcon = <PlusOutlined />,
    gradient = 'purple',
    showAction = !!onAction,
    size = 'medium'
}) => {

    // Define gradient colors
    const gradients = {
        purple: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        pink: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        blue: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        orange: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        green: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    };

    // Define size configurations
    const sizeConfig = {
        small: {
            iconCircle: 80,
            iconSize: 36,
            padding: '40px 20px',
            titleSize: '18px',
            descSize: '13px',
            maxWidth: '300px'
        },
        medium: {
            iconCircle: 100,
            iconSize: 48,
            padding: '60px 20px',
            titleSize: '20px',
            descSize: '14px',
            maxWidth: '350px'
        },
        large: {
            iconCircle: 120,
            iconSize: 60,
            padding: '80px 20px',
            titleSize: '24px',
            descSize: '15px',
            maxWidth: '400px'
        }
    };

    const config = sizeConfig[size] || sizeConfig.medium;
    const selectedGradient = gradients[gradient] || gradients.purple;

    // Choose icon component
    const IconComponent = icon === 'inbox' ? InboxOutlined : InboxOutlined;

    return (
        <div
            className="pcu-flex pcu-flex-col pcu-items-center pcu-justify-center pcu-text-center"
            style={{
                padding: config.padding,
                whiteSpace: 'normal', // Crucial to override table cell 'nowrap'
                width: '100%'
            }}
        >
            {/* Icon Circle */}
            <div
                className={`pcu-radius-full pcu-flex pcu-items-center pcu-justify-center pcu-margin-bottom-20 pcu-shadow-sm`}
                style={{
                    width: `${config.iconCircle}px`,
                    height: `${config.iconCircle}px`,
                    background: selectedGradient,
                    "--pcu-shadow-color": gradient === 'purple' ? 'rgba(102, 126, 234, 0.3)' :
                        gradient === 'pink' ? 'rgba(245, 87, 108, 0.3)' :
                            gradient === 'blue' ? 'rgba(79, 172, 254, 0.3)' :
                                gradient === 'orange' ? 'rgba(250, 112, 154, 0.3)' :
                                    'rgba(67, 233, 123, 0.3)',
                    boxShadow: `0 8px 24px var(--pcu-shadow-color)`,
                    flexShrink: 0
                }}
            >
                <IconComponent className="pcu-text-white" style={{ fontSize: `${config.iconSize}px` }} />
            </div>

            {/* Title */}
            <h3
                className="pcu-font-700 pcu-text-title pcu-margin-0 pcu-margin-bottom-12"
                style={{
                    fontSize: config.titleSize,
                    color: 'var(--color-text-title)'
                }}
            >
                {title}
            </h3>

            {/* Description */}
            <p
                className="pcu-text-muted pcu-line-height-1-6 pcu-margin-0"
                style={{
                    fontSize: config.descSize,
                    marginBottom: showAction ? '24px' : '0',
                    maxWidth: config.maxWidth,
                }}
            >
                {description}
            </p>

            {/* Action Button */}
            {showAction && onAction && (
                <Button
                    type="primary"
                    icon={actionIcon}
                    onClick={onAction}
                    className="pcu-border-none pcu-radius-8 pcu-h-auto pcu-font-600 pcu-transition-all"
                    style={{
                        background: selectedGradient,
                        padding: size === 'large' ? '12px 36px' : '10px 28px',
                        fontSize: size === 'large' ? '15px' : '14px',
                        boxShadow: `0 6px 16px var(--pcu-shadow-color)`,
                        height: '42px'
                    }}
                >
                    {actionText}
                </Button>
            )}
        </div>
    );
};

export default EmptyState;
